"use client";

import { useEffect, useRef, useState } from "react";
import { CountUp } from "countup.js";

/** Parses strings like "150+", "06+", "12+" → { end: 150, suffix: "+" } */
export function parseStatValue(val: string | number): {
  end: number;
  prefix: string;
  suffix: string;
  decimalPlaces: number;
} {
  if (typeof val === "number") {
    return { end: val, prefix: "", suffix: "", decimalPlaces: 0 };
  }
  const str = String(val).trim();
  // Extract leading non-numeric prefix (e.g. "$")
  const prefixMatch = str.match(/^([^0-9]*)/);
  const prefix = prefixMatch ? prefixMatch[1] : "";
  // Extract trailing non-numeric suffix (e.g. "+", "k", "%")
  const suffixMatch = str.match(/([^0-9.]*)$/);
  const suffix = suffixMatch ? suffixMatch[1] : "";
  // Extract the numeric part
  const numStr = str.replace(prefix, "").replace(suffix, "").replace(/,/g, "");
  const num = parseFloat(numStr);
  const decimalPlaces = numStr.includes(".") ? numStr.split(".")[1].length : 0;
  return {
    end: isNaN(num) ? 0 : num,
    prefix,
    suffix,
    decimalPlaces,
  };
}

interface CountUpNumberProps {
  value: string | number;
  duration?: number;
  className?: string;
}

export default function CountUpNumber({
  value,
  duration = 3,
  className,
}: CountUpNumberProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [hasStarted, setHasStarted] = useState(false);

  // Trigger on scroll into view
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasStarted]);

  // Start CountUp once triggered
  useEffect(() => {
    if (!hasStarted || !ref.current) return;
    const { end, prefix, suffix, decimalPlaces } = parseStatValue(value);
    const countUp = new CountUp(ref.current, end, {
      duration,
      prefix,
      suffix,
      decimalPlaces,
      useGrouping: false,
    });
    if (!countUp.error) {
      countUp.start();
    } else {
      console.error(countUp.error);
    }
  }, [hasStarted, value, duration]);

  return (
    <span ref={ref} className={className}>
      0
    </span>
  );
}
